'use client';

import React, { useState } from 'react';
import { CoinProps } from '@/types/coins';
import { HistoryDataProps } from '@/types/coins/history';
import Section from '@/components/(postTypes)/symbols/Section';
import MainContent from '@/components/(blog)/MainContent';
import { PageContent } from '@/app/(site)/(postTypes)/symbol/[slug]/page';
import { ImageNode } from '@/types/global';
import Image from 'next/image';
import SingleCoinIntro from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinIntro';
import PostComments from '@/components/(blog)/post/PostComments';
import {
    CommentNode,
    CommentsData,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reviews';
import { DocumentText, ArrowDown2 } from 'iconsax-react';
import PostFAQ from '@/components/(blog)/post/PostFAQ';
import PostToc from '@/components/(blog)/post/PostToc';
import { extractTocItems } from '@/utils/post/extractTocItems';

interface SingleCoinHeader extends CoinProps, HistoryDataProps, PageContent {
    slug: string;
    timePeriod: string;
    updateTimePeriod: (newTimePeriod: string) => void;
    displayName: string;
    commentCount: number;
    databaseId: number;
    featuredImage?: ImageNode;
    comments?: CommentNode[];
    hasNextPage?: CommentsData['comments']['pageInfo']['hasNextPage'];
}

const ContentSection: React.FC<SingleCoinHeader> = ({
    nameFa,
    symbol,
    rank,
    price,
    name,
    change,
    displayName,
    supply,
    marketCap,
    dominance,
    volume24h,
    content,
    posts_acf,
    commentCount,
    databaseId,
    postRatings,
    featuredImage,
    hasNextPage,
    comments,
    typeId,
    price_usd,
    quote_currency,
}) => {
    const { tocItems } = extractTocItems(content || '');
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <div
            id='blog-content-section'
            className='order-8 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
        >
            {/* Main Unified Content Section: Intro + Stat Cards + Main Content Article + FAQ */}
            <div
                id='intro-content-section'
                className='bg-gray-100/70 dark:bg-white/5 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active'
            >
                <Section title={`معرفی و آمار کلیدی ${displayName}`} className='max-lg:mt-0 mb-4'>
                    <SingleCoinIntro
                        change={change}
                        marketCap={marketCap}
                        name={name || ''}
                        nameFa={nameFa}
                        supply={supply}
                        price={price}
                        price_usd={price_usd}
                        quote_currency={quote_currency}
                        rank={rank}
                        dominance={dominance}
                        symbol={symbol || ''}
                        volume24h={volume24h}
                        typeId={typeId}
                    />
                </Section>

                {content && (
                    <div className='relative pt-4 border-t border-gray-100 dark:border-white/5'>
                        <div
                            className={`relative transition-all duration-500 ease-in-out overflow-hidden ${
                                isExpanded ? 'max-h-none pb-12' : 'max-h-[360px]'
                            }`}
                        >
                            {featuredImage && (
                                <Image
                                    width={1200}
                                    height={800}
                                    src={featuredImage?.node?.sourceUrl || ''}
                                    title={featuredImage?.node?.title || displayName}
                                    alt={featuredImage?.node?.title || displayName}
                                    className='aspect-video w-full rounded-2xl mb-6 object-cover shadow-sm'
                                />
                            )}

                            <article className='prose post-content max-w-none text-justify dark:text-white mx-auto'>
                                {tocItems && tocItems.length > 0 && (
                                    <PostToc
                                        tocItems={tocItems}
                                        className='bg-blue-50/50 dark:bg-white/5 border border-blue-100 dark:border-white/10 p-4 rounded-xl mb-6'
                                    />
                                )}
                                <MainContent
                                    processedContent={content || ''}
                                    className={undefined}
                                    title={nameFa || name || ''}
                                />
                                {posts_acf?.faqList && posts_acf.faqList.length > 0 && (
                                    <PostFAQ faqList={posts_acf.faqList} />
                                )}
                            </article>

                            {!isExpanded && (
                                <div className='absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-100/90 via-gray-100/70 to-transparent lg:from-white lg:via-white/80 dark:from-primary-active dark:via-primary-active/80 pointer-events-none' />
                            )}
                        </div>

                        <button
                            type='button'
                            onClick={() => setIsExpanded((prev) => !prev)}
                            className='w-fit flex items-center justify-center text-center mx-auto bg-blue-50 dark:bg-white/10 hover:bg-blue-100 dark:hover:bg-white/15 text-blue-600 dark:text-white cursor-pointer transition-all gap-2 border border-blue-200 dark:border-white/10 rounded-xl py-2.5 px-5 shadow-sm text-sm font-medium z-10 sticky bottom-4 mt-4'
                        >
                            <DocumentText size='20' className='shrink-0' />
                            <span>
                                {isExpanded
                                    ? 'بستن متن کامل'
                                    : `مشاهده مقاله و معرفی کامل ${nameFa || name}`}
                            </span>
                            <ArrowDown2
                                size='16'
                                className={`transition-transform duration-300 ${
                                    isExpanded ? 'rotate-180' : ''
                                }`}
                            />
                        </button>
                    </div>
                )}
            </div>

            {/* User Reviews & Comments Section */}
            <div
                id='comments-section'
                className='bg-white dark:bg-primary-active -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm'
            >
                <Section title={`نظرات و دیدگاه کاربران درباره ${displayName}`}>
                    <PostComments
                        comments={comments}
                        hasNextPage={hasNextPage}
                        displayName={displayName}
                        currentPostId={databaseId || 0}
                        postRatings={postRatings}
                        commentCount={commentCount || 0}
                    />
                </Section>
            </div>
        </div>
    );
};

export default ContentSection;
